import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import type { FacilityActions, FacilityFilterConfig, FacilityState } from '@/stores/types/facilityStore';
import { resolveKioskPriorityBuilding } from '@/utils/kiosk-building-priority';
import poiUtils from '@/utils/poi';
import facilityUtils from '@/utils/facility';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';
import type { PoiContentSlide } from '@/types/shop';

interface FacilityStore extends FacilityState, FacilityActions { }

export const useFacilityStore = create<FacilityStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            rawFacilityContents: [],
            facilityContents: [],
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR,
            keyboardLanguage: 'ko',

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;

                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: FACILITY_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;

                set({ currentMode: mode });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | string[] | null } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;

                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;

                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev === category) return;

                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                const prev = get().t;
                if (prev === t) return;

                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;
                const { config } = useConfigStore.getState();
                const {
                    mergedCategories: { facility },
                    server,
                } = config;

                const allFloorText = t ? t('facility.search.text.content.all_floor') : 'All Floors';
                const isMultipleBuildings = server?.store?.buildings && server?.store?.buildings?.length > 1;
                // Reset all filters to initial state
                set({
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: isMultipleBuildings ? 'all' : (server?.store?.buildings?.[0]?.id ?? 'all'),
                    selectedCategory: isMultipleBuildings ? facility?.[0] : server?.store?.buildings?.[0]?.filterCategories?.facility?.[0],
                });
            },

            updateFacilityContents: (searchMode: FACILITY_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { rawFacilityContents, selectedFloor, selectedBuildingId, selectedCategory } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language },
                } = config;

                // Find storeCode and buildingCode from selectedBuildingId
                let storeCode: string | null = null;
                let buildingCode: string | null = null;

                if (selectedBuildingId && selectedBuildingId !== 'all') {
                    const selectedBuilding = config.server?.store?.buildings?.find((b) => b.id === selectedBuildingId);
                    if (selectedBuilding) {
                        storeCode = selectedBuilding.storeCode;
                        buildingCode = selectedBuilding.buildingCode;
                    }
                }

                const filterConfig: FacilityFilterConfig = {
                    language,
                    selectedBuildingId,
                    storeCode,
                    buildingCode,
                    selectedFloor,
                    selectedCategory,
                    buildings: config.server?.store?.buildings,
                };

                const facilityFilter = facilityUtils.createFacilityFilter(searchMode, filterConfig);
                const filteredContents = rawFacilityContents?.filter(facilityFilter);

                const priorityBuilding = resolveKioskPriorityBuilding(
                    config.server?.store?.studioMapId,
                    config.server?.building,
                    config.server?.store?.buildings,
                );
                const sortedContents = poiUtils.getSortedContents([...filteredContents], language, priorityBuilding);

                set({ facilityContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const { config } = useConfigStore.getState();
                const {
                    ui: { language },
                    mergedCategories: { facility },
                    server,
                } = config;

                const rawContents = facilityUtils.convertPoiContentToSlide(language, server?.store?.buildings ?? []);
                const isMultipleBuildings = server?.store?.buildings && server?.store?.buildings?.length > 1;
                const selectedBuildingId = isMultipleBuildings ? 'all' : (server?.store?.buildings?.[0]?.id ?? 'all');
                const selectedCategory = isMultipleBuildings ? facility?.[0] : server?.store?.buildings?.[0]?.filterCategories?.facility?.[0];

                set({ t, rawFacilityContents: rawContents, currentMode: FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR, selectedBuildingId, selectedCategory });
            },
        }),
        {
            name: 'facility-store',
        }
    )
);

let previousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let previousLanguage: string = '';

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;

    if (currentBuildings !== previousBuildings || currentLanguage !== previousLanguage) {
        previousBuildings = currentBuildings;
        previousLanguage = currentLanguage;

        const { initialize, t } = useFacilityStore.getState();
        if (t) {
            initialize(t);
        }
    }
});
